import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCreateAssistantIAOperationMutation } from "actions/assistantIA/mutations";
import { useGetClientQuery } from "actions/clients/queries";
import { useGetComptePaiementQuery } from "actions/compte_paiement/queries";
import { useGetFournisseurQuery } from "actions/fournisseurs/queries";
import { useUploadOcrFileMutation } from "actions/orc/mutations";
import { useLazyExtractOcrDataQuery } from "actions/orc/queries";
import { useSuggestionsIntelligentesMutation } from "actions/rag_compte/mutations";
import OHADA_ACCOUNTS from "data/ohadaAccounts";
import { toastApiErrors } from "utils/toastApiError";
import "./AssistantIADetailPage.css";

const TAX_OPTIONS = [0, 10, 18];

const toNumber = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const pickFirstValue = (object, keys) => {
  if (!object || typeof object !== "object") return "";
  for (const key of keys) {
    const value = object[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== "") {
      return `${value}`.trim();
    }
  }
  return "";
};

const normalizeOcrData = (ocrData) => {
  const details = ocrData?.invoice_details || {};
  const totals = ocrData?.totals || {};
  const lineItemsSource = Array.isArray(ocrData?.line_items) ? ocrData.line_items : [];

  const line_items = lineItemsSource.map((item, index) => {
    const quantity = toNumber(item?.quantity || item?.qty || 1) || 1;
    const unit_price = toNumber(item?.unit_price || item?.price || item?.prix_unitaire || 0);
    const taxe = toNumber(item?.vat_rate || item?.tax_rate || item?.taxe || 0);
    const amount = toNumber(item?.total_amount || item?.amount || item?.total_ttc || 0);
    return {
      id: `${index + 1}`,
      description: `${item?.description || item?.label || item?.nom || "Ligne facture"}`,
      quantity,
      unit_price: unit_price || (quantity > 0 ? amount / quantity : 0),
      taxe: TAX_OPTIONS.includes(taxe) ? taxe : 0,
      amount,
    };
  });

  const computedTotal = line_items.reduce(
    (acc, item) => acc + toNumber(item.unit_price) * toNumber(item.quantity) * (1 + toNumber(item.taxe) / 100),
    0
  );

  const total_amount = toNumber(
    totals?.total_amount_due ||
      totals?.total_ttc ||
      totals?.total_amount ||
      details?.total_amount ||
      details?.montant_total ||
      computedTotal
  );

  const invoice_number = pickFirstValue(details, [
    "invoice_number",
    "invoice_ref",
    "reference",
    "numero_facture",
  ]);
  const invoice_date = pickFirstValue(details, ["invoice_date", "date", "date_facture"]);
  const supplier_name = pickFirstValue(details, ["supplier_name", "vendor_name", "fournisseur", "seller"]);
  const customer_name = pickFirstValue(details, ["customer_name", "client_name", "client", "buyer"]);

  return {
    raw: ocrData,
    invoice_number,
    invoice_date,
    supplier_name,
    customer_name,
    total_amount,
    line_items,
    first_tax: line_items.length > 0 ? line_items[0].taxe : 0,
  };
};

const NATURE_CONFIG = {
  vente: { label: "Vente", classes: ["7"] },
  achat: { label: "Achat", classes: ["6"] },
  immobilisation: { label: "Immobilisation", classes: ["2"] },
};

const DEFAULT_CODE_BY_NATURE = {
  vente: "701",
  achat: "601",
  immobilisation: "215",
};

const buildAccountOptions = (nature) => {
  const classes = NATURE_CONFIG[nature]?.classes || [];
  return OHADA_ACCOUNTS.filter((account) =>
    classes.length ? classes.some((klass) => String(account.code).startsWith(klass)) : true
  ).map((account) => ({
    code: String(account.code),
    label: `${account.code} - ${account.libelle}`,
  }));
};

const EMPTY_LINE = {
  nom_produit: "",
  description: "",
  quantite: 1,
  prix_unitaire: 0,
  nature: "vente",
  code_comptable: DEFAULT_CODE_BY_NATURE.vente,
  suggestions: [],
};

const INITIAL_OPERATION = {
  type: "entree",
  mode_paiement: "comptant",
  tierId: "",
  sourcePaiementId: "",
  intitule: "",
  description: "",
  date_transaction: "",
  taxes: 0,
  remise: 0,
  somme_payee: 0,
  lignes: [{ ...EMPTY_LINE }],
};

const AssistantIADetailPage = () => {
  const { clientID: dossierId } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState("");
  const [isPdfPreview, setIsPdfPreview] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [justificatifFile, setJustificatifFile] = useState(null);
  const [uploadedJustificatif, setUploadedJustificatif] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [activeSuggestLineIdx, setActiveSuggestLineIdx] = useState(null);
  const [operation, setOperation] = useState(INITIAL_OPERATION);

  const { data: clientsData } = useGetClientQuery({ dossier_id: dossierId }, { skip: !dossierId });
  const { data: fournisseursData } = useGetFournisseurQuery({ dossier_id: dossierId }, { skip: !dossierId });
  const { data: comptePaiementData } = useGetComptePaiementQuery({ dossier_id: dossierId }, { skip: !dossierId });

  const [uploadOcrFile, { isLoading: isUploading }] = useUploadOcrFileMutation();
  const [triggerExtractOcrData, { isFetching: isExtracting }] = useLazyExtractOcrDataQuery();
  const [suggestionsIntelligentes, { isLoading: isSuggesting }] = useSuggestionsIntelligentesMutation();
  const [createAssistantIAOperation, { isLoading: isSubmitting }] = useCreateAssistantIAOperationMutation();

  const clients = useMemo(() => (Array.isArray(clientsData?.data) ? clientsData.data : []), [clientsData]);
  const fournisseurs = useMemo(
    () => (Array.isArray(fournisseursData?.data) ? fournisseursData.data : []),
    [fournisseursData]
  );
  const comptesPaiement = useMemo(
    () => (Array.isArray(comptePaiementData?.data) ? comptePaiementData.data : []),
    [comptePaiementData]
  );

  const totalFacture = useMemo(() => {
    const montantHT = operation.lignes.reduce(
      (sum, item) => sum + toNumber(item.prix_unitaire) * toNumber(item.quantite),
      0
    );
    return montantHT * (1 + toNumber(operation.taxes) / 100) - montantHT * (toNumber(operation.remise) / 100);
  }, [operation.lignes, operation.taxes, operation.remise]);

  useEffect(() => {
    if (operation.mode_paiement !== "comptant") return;
    const totalArrondi = Math.max(0, Math.floor(toNumber(totalFacture)));
    if (toNumber(operation.somme_payee) === totalArrondi) return;
    setOperation((prev) => ({ ...prev, somme_payee: totalArrondi }));
  }, [operation.mode_paiement, operation.somme_payee, totalFacture]);

  useEffect(() => {
    return () => {
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    };
  }, [filePreviewUrl]);

  const currentTiers = operation.type === "entree" ? clients : fournisseurs;
  const currentTierLabel = operation.type === "entree" ? "Client" : "Fournisseur";
  const isScanRunning = isUploading || isExtracting;
  const selectedTier = currentTiers.find((item) => `${item.id}` === `${operation.tierId}`);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setFile(selectedFile);
    setExtracted(null);
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl("");
    }
    if (!selectedFile) return;
    const previewUrl = URL.createObjectURL(selectedFile);
    setFilePreviewUrl(previewUrl);
    setIsPdfPreview(selectedFile.type === "application/pdf");
  };

  const handleJustificatifChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    setJustificatifFile(selectedFile);
  };

  const handleUploadAndExtract = async () => {
    if (!file) {
      toast.warning("Veuillez sélectionner un fichier facture.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("dossier_client", dossierId);
      const uploadResponse = await uploadOcrFile(formData).unwrap();
      if (!uploadResponse?.id) {
        toast.error("Upload OCR échoué.");
        return;
      }
      setUploadedFile(uploadResponse);

      // Upload du justificatif de règlement si fourni
      let justificatifResponse = null;
      if (justificatifFile) {
        const justificatifFormData = new FormData();
        justificatifFormData.append("file", justificatifFile);
        justificatifFormData.append("dossier_client", dossierId);
        try {
          justificatifResponse = await uploadOcrFile(justificatifFormData).unwrap();
          if (justificatifResponse?.id) {
            setUploadedJustificatif(justificatifResponse);
          }
        } catch (err) {
          toastApiErrors(err);
        }
      }

      const ocrResponse = await triggerExtractOcrData(uploadResponse.id).unwrap();
      const normalized = normalizeOcrData(ocrResponse);
      setExtracted(normalized);

      const inferredType = normalized.supplier_name ? "sortie" : "entree";
      const firstLine = normalized.line_items[0] || {};
      const inferredIntitule = normalized.invoice_number
        ? `Facture ${normalized.invoice_number}`
        : inferredType === "entree"
          ? "Entrée depuis facture OCR"
          : "Sortie depuis facture OCR";
      const defaultNature = inferredType === "sortie" ? "achat" : "vente";

      setOperation((prev) => ({
        ...prev,
        type: inferredType,
        mode_paiement: "comptant",
        intitule: inferredIntitule,
        description: firstLine?.description || prev.description,
        date_transaction: normalized.invoice_date || prev.date_transaction,
        taxes: TAX_OPTIONS.includes(normalized.first_tax) ? normalized.first_tax : 0,
        somme_payee: normalized.total_amount || prev.somme_payee,
        lignes:
          normalized.line_items.length > 0
            ? normalized.line_items.map((item) => ({
                nom_produit: item.description || "Produit OCR",
                description: item.description || "",
                quantite: item.quantity || 1,
                prix_unitaire: item.unit_price || 0,
                nature: defaultNature,
                code_comptable: DEFAULT_CODE_BY_NATURE[defaultNature] || "",
                suggestions: [],
              }))
            : prev.lignes,
      }));

      toast.success("Extraction OCR réussie.");
      setStep(2);
    } catch (error) {
      toastApiErrors(error);
    }
  };

  const handleLineFieldChange = (index, name, value) => {
    setOperation((prev) => {
      const next = [...prev.lignes];
      next[index] = { ...next[index], [name]: value };
      if (name === "nature") {
        next[index].code_comptable = DEFAULT_CODE_BY_NATURE[value] || "";
      }
      return { ...prev, lignes: next };
    });
  };

  const addLine = () => {
    const defaultNature = operation.type === "sortie" ? "achat" : "vente";
    setOperation((prev) => ({
      ...prev,
      lignes: [
        ...prev.lignes,
        {
          ...EMPTY_LINE,
          nature: defaultNature,
          code_comptable: DEFAULT_CODE_BY_NATURE[defaultNature] || "",
        },
      ],
    }));
  };

  const removeLine = (index) => {
    setOperation((prev) => ({
      ...prev,
      lignes: prev.lignes.filter((_, i) => i !== index),
    }));
  };

  const requestAISuggestionsForLine = async (index) => {
    const line = operation.lignes[index];
    const natureLabel = NATURE_CONFIG[line?.nature]?.label || line?.nature || "";
    const contextText = [natureLabel, operation.intitule, line?.nom_produit, line?.description]
      .filter(Boolean)
      .join(" - ");

    if (contextText.trim().length < 3) {
      toast.info("Ajoutez plus de contexte pour les suggestions IA.");
      return;
    }

    try {
      setActiveSuggestLineIdx(index);
      const response = await suggestionsIntelligentes({
        description: contextText,
        dossier_id: dossierId,
        top_k: 3,
      }).unwrap();
      const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
      setOperation((prev) => {
        const next = [...prev.lignes];
        const shouldAutoFill = !next[index]?.code_comptable && suggestions.length > 0;
        next[index] = {
          ...next[index],
          suggestions,
          ...(shouldAutoFill ? { code_comptable: String(suggestions[0].code) } : {}),
        };
        return { ...prev, lignes: next };
      });
      if (!suggestions.length) toast.info("Aucune suggestion IA trouvée pour ce contexte.");
    } catch (error) {
      toastApiErrors(error);
    } finally {
      setActiveSuggestLineIdx(null);
    }
  };

  const validateStep2 = () => {
    if (!operation.tierId || !operation.sourcePaiementId || !operation.intitule || !operation.description) {
      toast.error("Complétez toutes les informations obligatoires.");
      return false;
    }
    if (!operation.lignes.length) {
      toast.error("Ajoutez au moins une ligne OCR.");
      return false;
    }
    const invalidLineIndex = operation.lignes.findIndex(
      (item) =>
        !String(item.nom_produit || "").trim() ||
        toNumber(item.prix_unitaire) <= 0 ||
        toNumber(item.quantite) <= 0 ||
        !String(item.code_comptable || "").trim()
    );
    if (invalidLineIndex >= 0) {
      const line = operation.lignes[invalidLineIndex];
      const missingFields = [];
      if (!String(line.nom_produit || "").trim()) missingFields.push("nom");
      if (toNumber(line.quantite) <= 0) missingFields.push("quantité");
      if (toNumber(line.prix_unitaire) <= 0) missingFields.push("prix unitaire");
      if (!String(line.code_comptable || "").trim()) missingFields.push("code comptable");
      toast.error(`Ligne ${invalidLineIndex + 1}: champs manquants/invalides (${missingFields.join(", ")}).`);
      return false;
    }
    if (toNumber(operation.somme_payee) < 0 || toNumber(operation.somme_payee) > totalFacture) {
      toast.error("Le montant payé doit être compris entre 0 et le total facture.");
      return false;
    }
    return true;
  };

  const handleGoToStep3 = () => {
    if (!validateStep2()) return;
    setStep(3);
  };

  const handleSubmitOperation = async () => {
    if (!validateStep2()) return;

    const payload = {
      type: operation.type,
      intitule: operation.intitule,
      dossier_id: Number(dossierId),
      tier_id: Number(operation.tierId),
      source_paiement: Number(operation.sourcePaiementId),
      taxes: toNumber(operation.taxes),
      description: operation.description,
      lignes: operation.lignes.map((item) => ({
        nom_produit: item.nom_produit,
        description: item.description || "",
        quantite: toNumber(item.quantite),
        prix_unitaire: toNumber(item.prix_unitaire),
        nature: item.nature,
        code_comptable: item.code_comptable,
      })),
      somme_payee: toNumber(operation.somme_payee),
      remise: toNumber(operation.remise),
      date_transaction: operation.date_transaction || null,
      ocr_file_id: uploadedFile?.id || null,
      justificatif_file_id: uploadedJustificatif?.id || null,
    };

    try {
      await createAssistantIAOperation(payload).unwrap();
      toast.success("Opération enregistrée avec succès.");
      navigate(`/client/${dossierId}/assistant-ia/`);
    } catch (error) {
      toastApiErrors(error);
    }
  };

  const goBackToList = () => navigate(`/client/${dossierId}/assistant-ia/`);

  return (
    <div className={`assistant-detail-root${step >= 2 ? " assistant-detail-root--form-step" : ""}`}>

      <div className="assistant-detail-header">
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn assistant-ia-btn-green" onClick={goBackToList}>
            ← Retour
          </button>
          <h4 style={{ margin: 0 }}>Assistant IA - Nouvelle opération</h4>
        </div>
        <div className="assistant-detail-steps">
          {[1, 2, 3].map((currentStep) => (
            <span
              key={currentStep}
              className="assistant-detail-step"
              style={{
                backgroundColor: step === currentStep ? "#009688" : "#e9ecef",
                color: step === currentStep ? "#fff" : "#6c757d",
              }}
            >
              Etape {currentStep}
            </span>
          ))}
        </div>
      </div>

      <div className="assistant-detail-grid">
        {/* COLONNE GAUCHE : facture (60%) */}
        <div className="assistant-detail-left">
          {!filePreviewUrl ? (
            <label className="assistant-detail-dropzone d-block" style={{ cursor: "pointer" }}>
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />
              <div style={{ fontSize: 40, lineHeight: 1 }}>🧾</div>
              <div style={{ fontWeight: 700, marginTop: 8 }}>Uploader la facture</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                Cliquez ici pour choisir une image ou un PDF, puis lancez le scan.
              </div>
            </label>
          ) : (
            <div className="assistant-detail-preview">
              <div
                className="d-flex align-items-center justify-content-between"
                style={{ padding: "8px 12px", fontSize: 13, fontWeight: 600 }}
              >
                <span className="text-truncate" style={{ maxWidth: "70%" }}>
                  {file?.name || "Facture"}
                </span>
                <label className="btn btn-sm assistant-ia-btn-green mb-0" style={{ cursor: "pointer" }}>
                  Changer
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf"
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
              <div className="assistant-detail-preview-stage">
                {isPdfPreview ? (
                  <iframe
                    title="preview-facture-pdf"
                    src={filePreviewUrl}
                    style={{ width: "100%", height: "100%", border: 0 }}
                  />
                ) : (
                  <img
                    src={filePreviewUrl}
                    alt="Prévisualisation facture"
                    style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain", display: "block" }}
                  />
                )}

                {isScanRunning && (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: "rgba(0, 150, 136, 0.12)",
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: -30,
                        height: 34,
                        background:
                          "linear-gradient(180deg, rgba(0,255,213,0.01) 0%, rgba(0,255,213,0.18) 60%, rgba(0,255,213,0.03) 100%)",
                        filter: "blur(1px)",
                        animation: "assistant-scan-line 1.3s linear infinite",
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: -10,
                        height: 8,
                        background:
                          "linear-gradient(90deg, rgba(0,191,165,0) 0%, rgba(0,255,213,1) 18%, rgba(224,255,250,1) 50%, rgba(0,255,213,1) 82%, rgba(0,191,165,0) 100%)",
                        boxShadow:
                          "0 0 10px rgba(0,255,213,0.95), 0 0 24px rgba(0,255,213,0.7), 0 0 36px rgba(0,191,165,0.5)",
                        animation: "assistant-scan-line 1.3s linear infinite",
                        pointerEvents: "none",
                      }}
                    />
                  </>
                )}
              </div>

              {step === 1 && (
                <div style={{ padding: 12 }}>
                  <Button
                    className="w-100 assistant-ia-btn-green"
                    onClick={handleUploadAndExtract}
                    disabled={!file || isScanRunning}
                  >
                    {isScanRunning ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Scan en cours...
                      </>
                    ) : (
                      "Scanner et extraire les informations"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* COLONNE DROITE : champs (40%) */}
        <div className="assistant-detail-right">
          {step === 1 && (
            <div style={{ background: "#f8f9fa", borderRadius: 12, padding: 16 }}>
              <h6 style={{ marginTop: 0 }}>Étape 1 - Scan de la facture</h6>
              <p style={{ color: "#6c757d", marginBottom: 12 }}>
                Uploadez la facture à gauche, prévisualisez-la, puis cliquez sur{" "}
                <strong>« Scanner et extraire »</strong>. L&apos;extraction intelligente remplira
                automatiquement les champs de l&apos;opération.
              </p>

              <div style={{ background: "#fff", border: "1px solid #e9ecef", borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>
                  Justificatif de règlement (preuve de paiement) <span style={{color: 'red'}}>*</span>
                </label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={handleJustificatifChange}
                  style={{ fontSize: 13 }}
                />
                {justificatifFile && (
                  <div style={{ fontSize: 12, color: "#198754", marginTop: 4 }}>
                    {justificatifFile.name}
                  </div>
                )}
                <div style={{ fontSize: 12, color: "#6c757d", marginTop: 4 }}>
                  Le justificatif de paiement est obligatoire. Veuillez joindre le reçu, le bordereau de versement ou tout document prouvant le règlement.
                </div>
              </div>

              {extracted && (
                <div style={{ background: "#fff", border: "1px solid #e9ecef", borderRadius: 8, padding: 12 }}>
                  <div><strong>Référence:</strong> {extracted.invoice_number || "-"}</div>
                  <div><strong>Date:</strong> {extracted.invoice_date || "-"}</div>
                  <div>
                    <strong>Total OCR:</strong>{" "}
                    {toNumber(extracted.total_amount).toLocaleString("fr-FR")}
                  </div>
                  <div><strong>Lignes extraites:</strong> {extracted.line_items.length}</div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="assistant-detail-form-step">
              <Row className="g-2">
                <Col xs={12} md={6} className="mb-3">
                  <Form.Label>Type d&apos;opération</Form.Label>
                  <Form.Select
                    value={operation.type}
                    onChange={(event) =>
                      setOperation((prev) => ({ ...prev, type: event.target.value, tierId: "" }))
                    }
                  >
                    <option value="entree">Entrée</option>
                    <option value="sortie">Sortie</option>
                  </Form.Select>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <Form.Label>{currentTierLabel}</Form.Label>
                  <Form.Select
                    value={operation.tierId}
                    onChange={(event) => setOperation((prev) => ({ ...prev, tierId: event.target.value }))}
                  >
                    <option value="">- Sélectionnez -</option>
                    {currentTiers
                      .filter((item) => item.active !== false && item.archived !== true)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.nom}
                        </option>
                      ))}
                  </Form.Select>
                </Col>
              </Row>

              <Row className="g-2">
                <Col xs={12} md={6} className="mb-3">
                  <Form.Label>Source de paiement</Form.Label>
                  <Form.Select
                    value={operation.sourcePaiementId}
                    onChange={(event) =>
                      setOperation((prev) => ({ ...prev, sourcePaiementId: event.target.value }))
                    }
                  >
                    <option value="">- Sélectionnez -</option>
                    {comptesPaiement.map((compte) => (
                      <option key={compte.id} value={compte.id}>
                        {compte.compte}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={operation.date_transaction}
                    onChange={(event) =>
                      setOperation((prev) => ({ ...prev, date_transaction: event.target.value }))
                    }
                  />
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Intitulé</Form.Label>
                <Form.Control
                  type="text"
                  value={operation.intitule}
                  onChange={(event) => setOperation((prev) => ({ ...prev, intitule: event.target.value }))}
                />
              </Form.Group>

              <div style={{ background: "#f8f9fa", borderRadius: 8, padding: "10px 12px", marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: "#6c757d" }}>Montant total</div>
                <div style={{ fontWeight: 700, fontSize: 18 }}>
                  {toNumber(totalFacture).toLocaleString("fr-FR")} FCFA
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Mode de paiement</Form.Label>
                <div className="assistant-detail-payment-modes">
                  <Button
                    type="button"
                    className={`assistant-ia-btn-green ${operation.mode_paiement === "comptant" ? "assistant-ia-btn-active" : ""}`}
                    onClick={() =>
                      setOperation((prev) => ({
                        ...prev,
                        mode_paiement: "comptant",
                        somme_payee: Math.max(0, Math.floor(toNumber(totalFacture))),
                      }))
                    }
                  >
                    Au comptant
                  </Button>
                  <Button
                    type="button"
                    className={`assistant-ia-btn-green ${operation.mode_paiement === "credit" ? "assistant-ia-btn-active" : ""}`}
                    onClick={() => setOperation((prev) => ({ ...prev, mode_paiement: "credit" }))}
                  >
                    À crédit
                  </Button>
                </div>
              </Form.Group>

              {operation.mode_paiement === "credit" && (
                <Form.Group className="mb-3">
                  <Form.Label>Montant payé</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={operation.somme_payee}
                    onChange={(event) => setOperation((prev) => ({ ...prev, somme_payee: event.target.value }))}
                  />
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  type="text"
                  value={operation.description}
                  onChange={(event) => setOperation((prev) => ({ ...prev, description: event.target.value }))}
                />
              </Form.Group>

              <Row className="g-2">
                <Col xs={12} sm={6} className="mb-3">
                  <Form.Label>Taxes</Form.Label>
                  <Form.Select
                    value={operation.taxes}
                    onChange={(event) => setOperation((prev) => ({ ...prev, taxes: Number(event.target.value) }))}
                  >
                    {TAX_OPTIONS.map((taxValue) => (
                      <option key={taxValue} value={taxValue}>
                        {taxValue}%
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12} sm={6} className="mb-3">
                  <Form.Label>Remise (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    value={operation.remise}
                    onChange={(event) => setOperation((prev) => ({ ...prev, remise: event.target.value }))}
                  />
                </Col>
              </Row>

              <div className="assistant-detail-lines-toolbar">
                <strong>Lignes OCR (produits)</strong>
                <Button size="sm" className="assistant-ia-btn-green" onClick={addLine}>
                  Ajouter une ligne
                </Button>
              </div>

              {operation.lignes.map((line, index) => {
                const accountOptions = buildAccountOptions(line.nature);
                return (
                  <div key={`line-${index}`} className="assistant-detail-line-card">
                    <Form.Control
                      className="mb-2"
                      type="text"
                      placeholder="Nom du produit (OCR)"
                      value={line.nom_produit}
                      onChange={(event) => handleLineFieldChange(index, "nom_produit", event.target.value)}
                    />
                    <Row className="g-2 mb-2">
                      <Col xs={12} sm={6}>
                        <Form.Control
                          type="number"
                          min="1"
                          placeholder="Quantité"
                          value={line.quantite}
                          onChange={(event) => handleLineFieldChange(index, "quantite", event.target.value)}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Form.Control
                          type="number"
                          min="0"
                          placeholder="Prix unitaire"
                          value={line.prix_unitaire}
                          onChange={(event) => handleLineFieldChange(index, "prix_unitaire", event.target.value)}
                        />
                      </Col>
                    </Row>

                    <div style={{ borderTop: "1px solid #dee2e6", margin: "8px 0" }} />

                    <Row className="g-2">
                      <Col xs={12}>
                        <Form.Select
                          value={line.nature}
                          onChange={(event) => handleLineFieldChange(index, "nature", event.target.value)}
                        >
                          <option value="vente">Vente</option>
                          <option value="achat">Achat</option>
                          <option value="immobilisation">Immobilisation</option>
                        </Form.Select>
                      </Col>
                      <Col xs={12}>
                        <Form.Select
                          value={line.code_comptable}
                          onChange={(event) => handleLineFieldChange(index, "code_comptable", event.target.value)}
                        >
                          <option value="">Code comptable</option>
                          {accountOptions.map((option) => (
                            <option key={`${index}-${option.code}`} value={option.code}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col xs={12}>
                        <Form.Control
                          type="text"
                          placeholder="Description de la ligne"
                          value={line.description || ""}
                          onChange={(event) => handleLineFieldChange(index, "description", event.target.value)}
                        />
                      </Col>
                      <Col xs={12} sm={6}>
                        <Button
                          size="sm"
                          className="w-100 assistant-ia-btn-green"
                          onClick={() => requestAISuggestionsForLine(index)}
                          disabled={isSuggesting && activeSuggestLineIdx === index}
                        >
                          {isSuggesting && activeSuggestLineIdx === index ? "IA..." : "Suggérer IA"}
                        </Button>
                      </Col>
                      <Col xs={12} sm={6}>
                        <Button
                          size="sm"
                          className="w-100 assistant-ia-btn-green"
                          onClick={() => removeLine(index)}
                          disabled={operation.lignes.length === 1}
                        >
                          Supprimer
                        </Button>
                      </Col>
                    </Row>

                    {!!line.suggestions?.length && (
                      <div className="d-flex gap-2 flex-wrap mt-2">
                        {line.suggestions.map((suggestion) => (
                          <button
                            key={`${index}-${suggestion.code}-${suggestion.libelle}`}
                            type="button"
                            className={`assistant-ia-suggestion-chip ${
                              line.code_comptable === String(suggestion.code) ? "is-selected" : ""
                            }`}
                            onClick={() => handleLineFieldChange(index, "code_comptable", String(suggestion.code))}
                          >
                            {suggestion.code} - {suggestion.libelle}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="assistant-detail-actions">
                <Button className="assistant-ia-btn-green" onClick={() => setStep(1)}>
                  Retour
                </Button>
                <Button className="assistant-ia-btn-green" onClick={handleGoToStep3}>
                  Continuer
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="assistant-detail-form-step">
              <div className="assistant-detail-summary">
                <h6>Vérification avant soumission</h6>
                <div><strong>Fichier:</strong> {file?.name || "-"}</div>
                <div><strong>Type:</strong> {operation.type === "entree" ? "Entrée" : "Sortie"}</div>
                <div><strong>{currentTierLabel}:</strong> {selectedTier?.nom || "-"}</div>
                <div>
                  <strong>Mode de paiement:</strong>{" "}
                  {operation.mode_paiement === "comptant" ? "Au comptant" : "À crédit"}
                </div>
                <div>
                  <strong>Montant payé:</strong> {toNumber(operation.somme_payee).toLocaleString("fr-FR")}
                </div>
                <div>
                  <strong>Montant total facture:</strong> {toNumber(totalFacture).toLocaleString("fr-FR")}
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong>Lignes validées:</strong>
                  <ul style={{ marginTop: 6, marginBottom: 0 }}>
                    {operation.lignes.map((line, idx) => (
                      <li key={`summary-line-${idx}`}>
                        {line.nom_produit} - Qté {line.quantite} x {line.prix_unitaire} - Compte{" "}
                        {line.code_comptable}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="assistant-detail-actions">
                <Button className="assistant-ia-btn-green" onClick={() => setStep(2)}>
                  Retour
                </Button>
                <Button className="assistant-ia-btn-green" onClick={handleSubmitOperation} disabled={isSubmitting}>
                  {isSubmitting ? "Enregistrement..." : "Soumettre l'opération"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssistantIADetailPage;
